import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus } from "lucide-react";
import { SectionHead, Reveal, EASE } from "./Reveal";

const FAQS = [
  {
    q: "Is this bootcamp beginner-friendly?",
    a: "Yes. The bootcamp is designed for complete beginners. No coding, design or marketing experience is required — every workflow is demonstrated step by step.",
  },
  {
    q: "Will both sessions be live?",
    a: "Yes. Both days are taught live online, and each session ends with a live Q&A so you can ask questions directly.",
  },
  {
    q: "Which language will be used?",
    a: "The sessions are taught in Hinglish — a comfortable mix of Hindi and English.",
  },
  {
    q: "Do I need paid AI tools?",
    a: "No. Everything is demonstrated with free AI tools and free plans, including ChatGPT and Canva.",
  },
  {
    q: "How will I receive access?",
    a: "After your registration is complete, you will receive joining instructions and community details on WhatsApp and email.",
  },
  {
    q: "Will recordings be provided?",
    a: "This is a live training and recordings are not included with the registration. If that changes for a batch, enrolled participants will be informed.",
  },
  {
    q: "What will I learn in two days?",
    a: "Day 1 covers digital product fundamentals, niche research and creating products with AI. Day 2 covers pricing, landing pages, payments, Instagram marketing and a 7-day launch plan.",
  },
  {
    q: "Is there any additional mentorship program?",
    a: "An optional advanced DPA live mentorship program may be introduced during the bootcamp. It is completely separate, with its own enrollment and pricing — it is not included in the bootcamp registration.",
  },
  {
    q: "Are earnings guaranteed?",
    a: "No. The bootcamp teaches practical skills and processes, but individual results vary and no income is guaranteed.",
  },
  {
    q: "What is the refund policy?",
    a: "For any refund-related questions, please message us on WhatsApp at +91 97623 42196 before enrolling and we will clarify the current policy.",
  },
];

export default function Faq() {
  const [open, setOpen] = useState(0);

  return (
    <section id="faq" data-testid="faq-section" className="bg-[#F6F1FF] py-20 sm:py-28">
      <div className="mx-auto max-w-3xl px-5 sm:px-8">
        <SectionHead
          eyebrow="FAQs"
          title={<>Frequently Asked <span className="bg-gradient-to-r from-[#9B5CF0] to-[#6D28D9] bg-clip-text text-transparent">Questions.</span></>}
          testId="faq"
        />
        <div className="mt-12 space-y-4">
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <Reveal key={f.q} delay={0.05 * i} y={16}>
                <div
                  data-testid={`faq-accordion-item-${i}`}
                  className={`overflow-hidden rounded-2xl border bg-white transition-[border-color,box-shadow] duration-300 ${
                    isOpen ? "border-[#7C3AED]/40 shadow-[0_20px_40px_-20px_rgba(124,58,237,0.3)]" : "border-[#E9DFFC]"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    aria-expanded={isOpen}
                    data-testid={`faq-question-${i}`}
                    className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
                  >
                    <span className="text-[15px] font-bold text-[#171321] sm:text-base">{f.q}</span>
                    <motion.span
                      animate={{ rotate: isOpen ? 45 : 0 }}
                      transition={{ duration: 0.25 }}
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors duration-300 ${
                        isOpen ? "bg-gradient-to-br from-[#9B5CF0] to-[#6D28D9] text-white" : "bg-[#F6F1FF] text-[#7C3AED]"
                      }`}
                    >
                      <Plus size={16} strokeWidth={2.5} />
                    </motion.span>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: EASE }}
                        className="overflow-hidden"
                      >
                        <p className="px-6 pb-6 text-sm leading-relaxed text-[#777181]" data-testid={`faq-answer-${i}`}>
                          {f.a}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
